<template>
  <HpModal
    :title="showThankYou ? '' : content?.header"
    id="exit-follow-up-modal"
    no-padding
    size="md"
    hide-footer
  >
    <section v-if="showThankYou" class="grid place-content-center gap-5 pb-7">
      <SiteSuccessAnimatedCheckmark />

      <p class="text-balance text-center leading-snug">
        {{ content?.thank_you_text }}
        <span class="mt-2 block text-4xl">Thank you!</span>
      </p>
    </section>

    <div v-else>
      <p class="-mt-2.5 text-balance text-base font-semibold leading-normal text-gray-800">
        {{ content?.text }}
      </p>

      <HpForm v-if="showCarrierInput" id="exit-follow-up-form" ref="form" hide-title class="my-6">
        <HpFormInput
          :label="content?.input_label"
          :placeholder="content?.input_placeholder"
          id="follow-up-modal-carrier-input"
          v-model="carrierInput"
        />
        <HpButton variant="primary" @click="onSubmit" class="mt-5">
          {{ content?.button_label }}
        </HpButton>
      </HpForm>

      <article
        v-else
        class="mx-auto mt-5 flex max-w-lg flex-col items-center gap-4 rounded-modal bg-primary-100 py-4 shadow-tooltip"
      >
        <HpAnchor
          :href="phoneNumber?.link"
          class="fluid-phone-number-size whitespace-nowrap px-5 pb-0.5 font-bold tracking-tight no-underline"
        >
          {{ phoneNumber?.number }} | TTY 711
          <small class="block text-center tracking-tight">{{ hours }}</small>
        </HpAnchor>

        <div class="w-full border-t border-border-dark">
          <ul
            class="mx-auto grid w-full max-w-fit grid-cols-1 gap-2 px-5 pt-3 sm:w-full sm:grid-cols-2"
          >
            <li
              v-for="item in content?.bullets"
              :key="item?.text"
              class="flex gap-1 text-left text-base"
            >
              <HpIcon :name="item?.icon" class="w-4 text-success" />
              {{ item?.text }}
            </li>
          </ul>
        </div>
      </article>
    </div>
  </HpModal>
</template>

<script lang="ts" setup>
  import { EngineStore } from '~/stores/engine'
  import DisplaySupportHours from '~/composables/DisplaySupportHours'
  import { DisplayPhoneNumber } from '~/composables/DisplayPhoneNumber'
  import type { ExitIntentContext } from '~/composables/ExitIntent'
  import { PlanType } from '~/generated/api-clients-generated'

  const props = defineProps({
    content: {
      type: Object,
      required: true
    },
    id: {
      type: String
    },
    context: {
      type: Object as PropType<ExitIntentContext>,
      default: () => ({} as ExitIntentContext)
    },
    showCarrierInput: {
      type: Boolean
    }
  })

  const { onSubmit, carrierInput, showThankYou } = await ExitIntent.use(props.context, props.id!)

  const engine = EngineStore.use()
  const planType = computed(() => engine?.selectedPlanType)

  const { hours } = await DisplaySupportHours.use()

  const phoneNumber = computed(() => {
    const mapd = props.content?.phone_mapd
    const pdp = props.content?.phone_pdp
    const ms = props.content?.phone_ms

    switch (true) {
      case planType.value === PlanType.MAPD:
        return {
          link: `tel:1-${mapd}`,
          number: DisplayPhoneNumber(mapd)
        }
      case planType.value === PlanType.PDP:
        return {
          link: `tel:1-${pdp}`,
          number: DisplayPhoneNumber(pdp)
        }
      case planType.value === PlanType.GAP:
        return {
          link: `tel:1-${ms}`,
          number: DisplayPhoneNumber(ms)
        }
    }
  })
</script>

<style scoped>
  .fluid-phone-number-size {
    @apply text-4xl xs:text-2xl;
    font-size: clamp(1.25rem, 6.5vw, 2.063rem);
  }
</style>
